import type { MouseEvent, PropsWithChildren, ReactNode } from "react";
import { useState } from "react";
import { tv } from "tailwind-variants";

import { TableCell } from "../table/table-cell";
import { TableRow } from "../table/table-row";
import { icons } from "./icons";

const expandableRowVariants = tv({
  slots: {
    row: "cursor-pointer hover:bg-surface-hover",
    toggle: [
      "-mx-1 -my-0.5 flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-left font-medium text-content-strong",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:outline-none",
    ],
    chevron: "size-4 shrink-0 text-content-muted transition-transform duration-150",
    detailCell: "bg-surface-sunken px-6 py-4",
  },
  variants: {
    isOpen: {
      true: { row: "bg-surface-hover [&>td]:border-b-0", chevron: "rotate-90" },
    },
  },
});

type ExpandableTableRowProps = {
  id: string;
  label: ReactNode;
  columnsLength: number;
  renderDetail: () => ReactNode;
};

export const ExpandableTableRow = ({
  children,
  columnsLength,
  id,
  label,
  renderDetail,
}: PropsWithChildren<ExpandableTableRowProps>) => {
  const [isOpen, setIsOpen] = useState(false);
  const { chevron, detailCell, row, toggle } = expandableRowVariants({ isOpen });
  const detailId = `${id}-detail`;

  const toggleDetail = () => {
    setIsOpen((open) => {
      return !open;
    });
  };

  const onRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
    if (event.target instanceof Element && event.target.closest("button, a")) {
      return;
    }

    toggleDetail();
  };

  return (
    <>
      <TableRow className={row()} onClick={onRowClick}>
        <TableCell>
          <button
            aria-controls={isOpen ? detailId : undefined}
            aria-expanded={isOpen}
            className={toggle()}
            onClick={toggleDetail}
            type="button"
          >
            <icons.ChevronRightIcon aria-hidden="true" className={chevron()} />

            {label}
          </button>
        </TableCell>

        {children}
      </TableRow>

      {isOpen ? (
        <TableRow id={detailId}>
          <TableCell className={detailCell()} colSpan={columnsLength}>
            {renderDetail()}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
};
